import React, { useEffect, useMemo, useState } from 'react';
import WhatsAppVerificationPage from './pages/WhatsAppVerificationPage';
import AuthPage from './pages/AuthPage';
import InputPage from './pages/InputPage';
import ResultPage from './pages/ResultPage';
import './App.css';
import RechargePage from './pages/RechargePage';
import RechargeModal from './components/RechargeModal';
import LandingPage from './pages/LandingPage';
import LGPDExclusionPage from './pages/LGPDExclusionPage';
import LGPDPrivacyPage from './pages/LGPDPrivacyPage';
import LGPDTermsPage from './pages/LGPDTermsPage';
import ConsultaAvulsaPage from './pages/ConsultaAvulsaPage';

// Componente de suporte via WhatsApp (botão flutuante com ícone oficial)
function WhatsAppSupport() {
  const number = ('551191931-9179').toString().trim();

  // SVG do ícone oficial do WhatsApp
  const WhatsAppIcon = () => (
    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="WhatsApp">
      <path fillRule="evenodd" clipRule="evenodd" d="M12.004 2C6.48 2 2 6.48 2 12.004c0 1.848.502 3.578 1.377 5.073L2 22l5.078-1.332A9.953 9.953 0 0012.004 22c5.523 0 10.004-4.48 10.004-10.004C22.008 6.48 17.527 2 12.004 2zm5.727 14.122c-.254.717-1.246 1.314-1.72 1.36-.464.047-.92.232-2.955-.572-2.45-.968-4.004-3.46-4.126-3.626-.122-.165-.992-1.32-.992-2.52 0-1.198.627-1.787.848-2.029.22-.242.485-.303.647-.303.162 0 .324.003.464.01.147.006.347-.056.543.416.202.485.69 1.683.75 1.805.061.121.101.263.02.424-.08.162-.121.263-.242.404-.122.142-.255.316-.364.425-.121.12-.249.252-.107.495.141.242.628 1.033 1.35 1.676.932.83 1.718 1.087 1.96 1.208.243.121.385.101.527-.06.141-.162.606-.707.768-.95.162-.242.323-.202.546-.12.222.08 1.414.666 1.657.787.242.121.404.182.464.283.06.101.06.586-.194 1.303z" fill="white" />
    </svg>
  );

  if (!number) {
    // Botão visível, mas desabilitado se o número não for informado
    const link = '#';
    return (
      <a href={link} className="whatsapp-fab" aria-label="Suporte via WhatsApp (indisponível)" title="Suporte via WhatsApp (indisponível)" style={{ opacity: 0.5, pointerEvents: 'none' }}>
        <WhatsAppIcon />
      </a>
    );
  }

  // Remove caracteres não numéricos para formar o link wa.me
  const cleaned = number.replace(/[^0-9]/g, '');
  const link = `https://wa.me/${cleaned}`;

  return (
    <a href={link} className="whatsapp-fab" target="_blank" rel="noreferrer" aria-label="Suporte via WhatsApp" title="Suporte via WhatsApp">
      <WhatsAppIcon />
    </a>
  );
}

export default function App() {

  const key = new URLSearchParams(window.location.search)?.get('key') || null;

  const storedToken = useMemo(() => localStorage.getItem('motocheckToken'), []);
  const [authToken, setAuthToken] = useState(storedToken);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(Boolean(storedToken));
  const [authMode, setAuthMode] = useState('login');
  const [authErro, setAuthErro] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tipo, setTipo] = useState(null);
  const [erro, setErro] = useState(null);
  const [parametro, setParametro] = useState("");
  const [needsRecharge, setNeedsRecharge] = useState(false);
  // Verification state for WhatsApp ownership (frontend)
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verMessage, setVerMessage] = useState(null);

  const path = window.location.pathname;

  const API_URL = process.env.REACT_APP_API_URL || '';
  useEffect(() => {
    if (path.startsWith('/register')) {
      setAuthMode('register');
    } else if (path.startsWith('/login')) {
      setAuthMode('login');
    }
  }, [path]);

  useEffect(() => {
    async function carregarSessao() {
      if (!authToken) {
        setAuthLoading(false);
        return;
      }

      try {
        const res = await fetch(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const data = await res.json();

        if (data.sucesso) {
          setUser(data.user);
          setAuthErro(null);
        } else {
          limparSessao();
        }
      } catch (error) {
        limparSessao();
      } finally {
        setAuthLoading(false);
      }
    }

    carregarSessao();
  }, [API_URL, authToken]);

  useEffect(() => {
    // Definimos a função async DENTRO do useEffect
    async function carregarConsulta() {
      const path = window.location.pathname;

      if (path.startsWith('/consulta-resultado')) {
        const key = new URLSearchParams(window.location.search).get('key');
        
        if (!key) {
          console.log('Chave de consulta não fornecida na URL.');
          return;
        }

        try {
          const res = await fetch(`${API_URL}/api/consulta-key`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ key }),
          });

          const data = await res.json();

          if (data.sucesso) {
            setResultado(data.resultado);
            setTipo('cpf');
            setUser({ email: "Teste@exemplo.com", empresa: "Teste", userGuest: true });
          } else {
            console.log(data.erro || 'Erro ao processar.');
          }
        } catch (e) {
          console.log('Não foi possível conectar à API. Verifique se o backend está rodando.');
        }
      }
    }

    carregarConsulta();
  }, []);

  function limparSessao() {
    window.location.href = '/';
    localStorage.removeItem('motocheckToken');
    setAuthToken(null);
    setUser(null);
    setResultado(null);
    setTipo(null);
    setParametro('');
    setErro(null);
    setLoading(false);
    setNeedsRecharge(false);
  }

  function salvarSessao(data) {
    localStorage.setItem('motocheckToken', data.token);
    setAuthToken(data.token);
    setUser(data.user);
    setAuthErro(null);
    setAuthMode('login');
    setResultado(null);
    setTipo(null);
    setParametro('');
    setErro(null);
    setNeedsRecharge(false);
  }

  function validarCpfOuCnpj(parametro) {
    const valor = String(parametro)

    // Remove qualquer caractere que não seja número
    const numeros = valor.replace(/\D/g, '');

    // Validação para CPF (11 dígitos)
    if (numeros.length === 11) {
      if (validarCPF(numeros)) {
        return { valido: true, tipo: 'CPF' };
      }
    }
    // Validação para CNPJ (14 dígitos)
    else if (numeros.length === 14) {
      if (validarCNPJ(numeros)) {
        return { valido: true, tipo: 'CNPJ' };
      }
    }

    // Se não tiver 11 nem 14 dígitos, ou falhar nas regras, é inválido
    return { valido: false, tipo: 'invalido' };
  }

  // --- Funções Auxiliares de Validação ---

  function validarCPF(cpf) {
    // Elimina CPFs com todos os dígitos iguais (ex: 111.111.111-11)
    if (/^(\d)\1{10}$/.test(cpf)) return false;

    // Validação do 1º Dígito Verificador
    let soma = 0;
    for (let i = 0; i < 9; i++) {
      soma += parseInt(cpf.charAt(i)) * (10 - i);
    }
    let resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(cpf.charAt(9))) return false;

    // Validação do 2º Dígito Verificador
    soma = 0;
    for (let i = 0; i < 10; i++) {
      soma += parseInt(cpf.charAt(i)) * (11 - i);
    }
    resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(cpf.charAt(10))) return false;

    return true;
  }

  function validarCNPJ(cnpj) {
    // Elimina CNPJs com todos os dígitos iguais
    if (/^(\d)\1{13}$/.test(cnpj)) return false;

    // Validação do 1º Dígito Verificador
    let tamanho = cnpj.length - 2;
    let numeros = cnpj.substring(0, tamanho);
    let digitos = cnpj.substring(tamanho);
    let soma = 0;
    let pos = tamanho - 7;

    for (let i = tamanho; i >= 1; i--) {
      soma += parseInt(numeros.charAt(tamanho - i)) * pos--;
      if (pos < 2) pos = 9;
    }
    let resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    if (resultado !== parseInt(digitos.charAt(0))) return false;

    // Validação do 2º Dígito Verificador
    tamanho = tamanho + 1;
    numeros = cnpj.substring(0, tamanho);
    soma = 0;
    pos = tamanho - 7;

    for (let i = tamanho; i >= 1; i--) {
      soma += parseInt(numeros.charAt(tamanho - i)) * pos--;
      if (pos < 2) pos = 9;
    }
    resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    if (resultado !== parseInt(digitos.charAt(1))) return false;

    return true;
  }

  async function authRequest(endpoint, payload) {
    setAuthErro(null);
    const res = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    // expose status to caller so we can handle special cases like 403
    return { status: res.status, data };
  }

  const [pendingWhatsApp, setPendingWhatsApp] = useState(null);
  const [showAuthFromLanding, setShowAuthFromLanding] = useState(false);

  async function handleLogin(payload) {
    setAuthLoading(true);
    try {
      const resp = await authRequest('/api/auth/login', payload);
      const status = resp.status;
      const data = resp.data;


      if (resp.status === 200 && resp.data?.success) {
        salvarSessao(resp.data);
        return;
      }

      if (resp.status === 403 && resp.data?.needsVerification) {
        setPendingWhatsApp(resp.data.whatsapp);
        return;
      }

      setAuthErro(data?.erro || 'Nao foi possivel entrar.');
    } finally {
      setAuthLoading(false);
    }

  }

  async function handleRegister(payload) {
    setAuthLoading(true);
    try {
      const { data } = await authRequest('/api/auth/register', payload);
      if (data.success) {
        if (data.needsVerification) {
          setPendingWhatsApp(data.whatsapp);
          return;
        }
      } else {
        setAuthErro(data.erro || 'Nao foi possivel registrar.');
      }
    } catch (error) {
      setAuthErro('Nao foi possivel conectar ao backend.');
    } finally {
      setAuthLoading(false);
    }
  }

  async function logout() {
    if (authToken) {
      try {
        await fetch(`${API_URL}/api/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${authToken}` },
        });
      } catch (error) {
        // limpeza local continua mesmo se o logout remoto falhar
      }

    }

    limparSessao();
  }

  function onLogin() {
    setShowAuthFromLanding(true);
    setAuthMode('login');
  }

  function onCreateAccount() {
    setShowAuthFromLanding(true);
    setAuthMode('register');
  }
  function onSingleQuery() {
    window.location.href = '/consulta-avulsa';

  }

  // Simple client-side route for Recharge page or LGPD/ Landing pages
  if (typeof window !== 'undefined') {
    // LGPD routes
    if (path.startsWith('/consulta-avulsa')) {
      return (<><ConsultaAvulsaPage onCreateAccount={onCreateAccount} /><WhatsAppSupport /></>);
    }
    if (path.startsWith('/lgpd/exclusion')) {
      return (<><LGPDExclusionPage /><WhatsAppSupport /></>);
    }
    if (path.startsWith('/lgpd/privacy')) {
      return (<><LGPDPrivacyPage /><WhatsAppSupport /></>);
    }
    if (path.startsWith('/lgpd/terms')) {
      return (<><LGPDTermsPage /><WhatsAppSupport /></>);
    }
    if (path.startsWith('/register') && !user) {
      return (
        <>
          <AuthPage
            mode={authMode}
            onToggleMode={() => setAuthMode((current) => {
              setAuthErro(null);
              return current === 'login' ? 'register' : 'login';
            })}
            onLogin={handleLogin}
            onRegister={handleRegister}
            loading={authLoading}
            erro={authErro}
          />
          <WhatsAppSupport />
        </>
      );
    }
    // Recharge or Landing page
    if (path.startsWith('/recargas')) {
      return <><RechargePage onLogout={logout} /><WhatsAppSupport /></>;
    }
    if (path === '/' && !authToken && !showAuthFromLanding) {
      // Landing page as the initial screen for presenting the product
      return (<><LandingPage onGetStarted={() => setShowAuthFromLanding(true)} onCreateAccount={onCreateAccount} onLogin={onLogin} onSingleQuery={onSingleQuery} /><WhatsAppSupport /></>);
    }
  }

  async function analisar(parametro) {
    if (!authToken) {
      setErro('Faça login para pesquisar.');
      return;
    }
    const price = validarCpfOuCnpj(parametro).valido ? 300 : 100;
    const credits = user?.credits || 0;
    if (credits < price) {
      setErro('Créditos insuficientes. Faça uma recarga para continuar.');
      setNeedsRecharge(true);
      return;
    }

    setParametro(parametro);
    setLoading(true);
    setErro(null);
    try {
      const res = await fetch(`${API_URL}/api/consulta`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ parametro: String(parametro) }),
      });
      const data = await res.json();
      if (data.sucesso) {
        setResultado(data.resultado);
        if (data.user) {
          setUser(data.user);
        }
        let tipoDetectado = null;
        if (data?.resultado?.consumo?.cpf) {
          setTipo('cpf');
        } else if (data?.resultado?.consumo?.cnpj) {
          setTipo('cnpj');
        } else if (data?.resultado?.consumo?.nome) {
          setTipo('nome');
        } else if (data?.resultado?.consumo?.email) {
          setTipo('email');
        } else if (data?.resultado?.consumo?.telefone) {
          setTipo('telefone');
        } else if (data?.resultado?.consumo?.cpf_parcial) {
          setTipo('cpf_parcial');
        } else {
          console.log('Tipo de dado não identificado na resposta');
        }
      } else {
        setErro(data.erro || 'Erro ao processar.');
        if (res.status === 401) {
          limparSessao();
        } else if (res.status === 402) {
          // saldo insuficiente: avise o usuário e sugira uma recarga
          setErro(data.erro || 'Você não possui créditos suficientes para consultar.');
          setNeedsRecharge(true);
        }
      }
    } catch (e) {
      setErro('Não foi possível conectar à API. Verifique se o backend está rodando.');
    } finally {
      setLoading(false);
    }
  }

  async function usarDemo() {
    if (!authToken) {
      setErro('Faça login para pesquisar.');
      return;
    }

    setLoading(true);
    setErro(null);
    try {
      const res = await fetch(`${API_URL}/api/analisar/demo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await res.json();
      if (data.sucesso) setResultado(data.resultado);
      if (res.status === 401) {
        limparSessao();
        setErro(data.erro || 'Sua sessao expirou.');
      }
    } catch (e) {
      setErro('Erro ao carregar demo.');
    } finally {
      setLoading(false);
    }
  }

  function voltar() {
    setResultado(null);
    setErro(null);
    setTipo(null);
    setParametro("");
    setNeedsRecharge(false);
  }

  if (authLoading) {
    return (
      <div className="appLoading">
        <div className="appLoadingCard">
          <span>Carregando sua sessao...</span>
        </div>
      </div>
    );
  }

  if (pendingWhatsApp) {
    return (
      <>
        <WhatsAppVerificationPage
          whatsapp={pendingWhatsApp}
          authToken={authToken}
          onVerified={async (data) => {
            salvarSessao(data);
            setPendingWhatsApp(null);
            return;
          }}
          onLogout={logout}
        />
        <WhatsAppSupport /></>
    );
  }

  if (!user) {
    return (
      <>
        <AuthPage
          mode={authMode}
          onToggleMode={() => setAuthMode((current) => {
            setAuthErro(null)
            return (current === 'login' ? 'register' : 'login')
          })
          }
          onLogin={handleLogin}
          onRegister={handleRegister}
          loading={authLoading}
          erro={authErro}
        />
        <WhatsAppSupport /></>
    );
  }

  return (
    tipo === 'cpf' || tipo === 'cnpj' ? (
      <ResultPage resultado={resultado} onVoltar={voltar} user={user} onLogout={logout} bearerToken={authToken} />
    ) : (
      <>
        {needsRecharge && (
          <RechargeModal
            onClose={() => setNeedsRecharge(false)}
            onGoRecharge={() => { setNeedsRecharge(false); window.location.href = '/recargas'; }}
          />
        )}
        <InputPage
          onAnalisar={analisar}
          onDemo={usarDemo}
          loading={loading}
          erro={erro}
          resultado={resultado}
          parametro={parametro}
          tipo={tipo}
          user={user}
          onLogout={logout}
        />
        <WhatsAppSupport />
      </>
    ));
}
